import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import {Span} from 'components/atoms/Text/Typography/Typography';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import utils from 'helpers/utils';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import Add from '@mui/icons-material/Add';
import StyledSaveSearchDialog from 'components/organisms/Dialogs/SaveSearchDialog/SaveSearchDialog.styles';
import SaveSearchWizard from 'components/organisms/Wizards/SaveSearchWizard/SaveSearchWizard';
import constants from 'helpers/constants';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import Save from '@mui/icons-material/Save';

const SaveSearchDialog = React.forwardRef((props, ref) => {
  const {
    title,
    subtitle,
    onClose,
    onSubmit,
    tableProvider,
    SaveSearchWizardProps,
    ...innerProps
  } = useComponentProps(props, 'SaveSearchDialog');

  const innerRef = useRef(null);
  const wizardRef = useRef(null);
  const selectedRef = useRef({});
  const [internalState, setInternalState] = useState({
    error: null,
    type: null,
    entityIds: null,
    isPrevEnabled: false,
    isNextEnabled: false
  });

  useImperativeHandle(ref, () => innerRef.current);

  const resultsCount = Math.min(tableProvider.listSelection.max, tableProvider.list?.meta?.resultsCount);

  const snackbar = useSnackbar();
  const dialogControl = useDialogControl();

  const entitySelectedEvent = useEffectEvent(tableProvider.updaters?.selectedItems);

  const backButtonAction = useMemo(() => ({
    label: 'Back',
    icon: ChevronLeft,
    ButtonProps: {
      color: 'primary',
      variant: 'text'
    },
    auth: internalState.isSubmitting ? utils.createAuth({attribute: 'system.null'}) : null,
    onClick: (e) => {
      wizardRef.current?.goToPrevStep?.();
      e.preventDefault();
    }
  }), [internalState.isSubmitting]);

  const nextButtonAction = useMemo(() => {
    const showNext = (internalState.isNextEnabled || !internalState.isSubmitEnabled);

    const [label, icon, color] = showNext ? ['Next', ChevronRight, 'primary'] : (
      internalState.type === constants.query.saveTypes.collection ? ['Add to collection', Add, 'success'] : (
        internalState.type === constants.query.saveTypes.source ? ['Create source', Add, 'success'] : (
          Boolean(tableProvider.view?.userView) ? ['Save view', Save, 'primary'] : ['Create view', Add, 'success']
        )
      )
    );

    return {
      label: label,
      icon: icon,
      iconPosition: showNext ? 'end' : 'start',
      ButtonProps: {
        color: color,
      },
      auth: (internalState.isSubmitting || (!internalState.isNextEnabled && !internalState.isSubmitEnabled)) ?
        utils.createAuth({attribute: 'system.null'}) : null,
      onClick: (e) => {
        wizardRef.current?.goToNextStep?.();
        e.preventDefault();
      }
    }
  }, [internalState.isNextEnabled, internalState.isSubmitEnabled, internalState.isSubmitting, internalState.type, tableProvider.view?.userView]);

  const handleChange = (isPrevEnabled, isNextEnabled, isSubmitEnabled, isSubmitting) => {
    const data = wizardRef.current?.data?.[wizardRef.current?.dataKey];
    setInternalState(utils.updater({
      isPrevEnabled,
      isNextEnabled,
      isSubmitEnabled,
      isSubmitting,
      type: data?.type?.value
    }, true));
  }

  const handleSubmit = (search, actions, onSuccess, onError) => {
    setInternalState(utils.updater({isSubmitting: false}, true));

    utils.asPromise(onSubmit)({
      ...search,
      selected: () => {
        return selectedRef.current;
      }
    })
      .then(() => {
        onSuccess?.();
        onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        onError?.();
        setInternalState(utils.updater({
          error: 'Saving search failed'
        }, true));
      })
      .finally(() => {
        setInternalState(utils.updater({isSubmitting: false}, true));
      });
  };

  const handleClose = (e, reason) => {
    if (!wizardRef.current?.isDirty()) {
      onClose?.(e, reason);
    } else if (['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      dialogControl.show(<ConfirmDialog question="Are you sure you want to exit this dialog?"
                                        explanation="The dialog will close and your changes will be lost"
                                        onConfirm={() => onClose?.(e, reason)}/>, true);
    }
  };

  const handleError = (error, validation) => {
    setInternalState(utils.updater({
      error: (error && !validation) ? error : null
    }, true));
  }

  const renderButtons = () => {
    return <React.Fragment>
      {internalState.isPrevEnabled ? <ActionButton key="back"
                                                   showInactive={true}
                                                   action={backButtonAction}/> : null}
      <ActionButton key="next"
                    showInactive={true}
                    action={nextButtonAction}/>
    </React.Fragment>
  }

  const renderInfo = () => {
    if (internalState.error) {
      return <Span color="error">{internalState.error}</Span>;
    }
  }

  const onCloseEvent = useEffectEvent(onClose);
  useEffect(() => {
    if (internalState.type === constants.query.saveTypes.collection) {
      if (!utils.isDefined(selectedRef.current.entities)) {
        entitySelectedEvent?.(tableProvider.listState, 0, resultsCount, null, false, true,
          (entities) => {
            selectedRef.current.entities = entities;
          }, () => {
            selectedRef.current.failed = true;
            onCloseEvent?.(null, 'closeButtonClick');
            snackbar.show('Retrieving selection data failed', null,
              {color: 'error', autoHideDuration: constants.delay.error});
          });
      }
    }
  }, [internalState.type, internalState.entityIds, entitySelectedEvent,
    tableProvider.listState, resultsCount, snackbar, onCloseEvent]);

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledSaveSearchDialog ref={innerRef} {...innerProps}
                                 size="large"
                                 onClose={handleClose}>
    <DialogHeader className="SaveSearchDialog-header"
                  title={title ?? 'Save search'}
                  subtitle={subtitle}/>
    <DialogContent className="SaveSearchDialog-content">
      <SaveSearchWizard ref={wizardRef}
                        variant="simple"
                        tableProvider={tableProvider}
                        className="SaveSearchDialog-wizard"
                        showProgressBar={true}
                        onError={handleError}
                        onSubmit={handleSubmit}
                        onChange={handleChange}
                        {...SaveSearchWizardProps} />
    </DialogContent>
    <DialogFooter className="SaveSearchDialog-footer"
                  variant="split"
                  info={renderInfo()}
                  buttons={renderButtons()} />
  </StyledSaveSearchDialog>
});

SaveSearchDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  count: PropTypes.number,
  title: PropTypes.any,
  subtitle: PropTypes.any,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  tableProvider: PropTypes.object,
  SaveSearchWizardProps: PropTypes.object
};

export default SaveSearchDialog;
